import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useApp, useSession, useUi } from '../app/context';
import { patchUi, ui } from '../app/ui';
import { docsOf, type SessionState, type WindowState } from '../core/session';
import { isUnprotected } from '../core/cloud';
import { splitName, type DocMeta } from '../core/documents';
import { MAX_WINDOWS } from '../core/geometry';
import { isReduced, OPTIC, play, RECEDE } from '../motion/motion';
import { CloseIcon, MoreIcon, PlusIcon, WorkspacesIcon } from './icons';
import { MenuItem, menuKeys, Popover } from './Popover';

/**
 * Workspaces on a phone.
 *
 * On a desktop each window presents one working context. A phone has no room
 * for windows, so the same contexts become pages you step back from: the
 * workspace key in the head pulls the writing back into a card among its
 * siblings — the page you were on shrinks into its place, the others rise
 * around it — and touching a card zooms back into it. Each card is a small
 * proof of the page (its active document's first lines), its name, and the
 * documents it holds, which open, close and move between workspaces from
 * here. Names are the person's own; geometry never matters here.
 *
 * Same data as the desktop: a workspace is a window (and, when signed in, a
 * synced grouping of its cloud documents). Nothing here invents a second model.
 */

export function workspaceName(w: Pick<WindowState, 'name'>, index: number): string {
  return w.name?.trim() || `Workspace ${index + 1}`;
}

/** The head's key on a compact canvas: which workspace you are in, and the way to the others. */
export function WorkspaceKey({ windowId }: { windowId: string }) {
  const count = useSession((s) => s.windows.length);
  const index = useSession((s) => s.windows.findIndex((w) => w.id === windowId));
  const name = useSession((s) => s.windows.find((w) => w.id === windowId)?.name);
  const open = useUi((s) => s.workspaces);
  const label = workspaceName({ name }, index);
  return (
    <button
      type="button"
      className="icon-btn ws-key"
      aria-label={`Workspaces — in ${label}${count > 1 ? `, ${count} workspaces` : ''}`}
      aria-haspopup="dialog"
      aria-expanded={open}
      onClick={() => patchUi({ workspaces: true, palette: false, contextMenu: null })}
    >
      <WorkspacesIcon size={17} />
      {count > 1 && <span className="ws-key-count">{count}</span>}
    </button>
  );
}

export function WorkspaceOverview() {
  const open = useUi((s) => s.workspaces);
  const compact = useUi((s) => s.compact);
  // The overview belongs to the compact canvas; a canvas that grows returns to windows.
  useEffect(() => {
    if (!compact && open) patchUi({ workspaces: false });
  }, [compact, open]);
  if (!open || !compact) return null;
  return <Overview />;
}

const viewportRect = () => ({ left: 0, top: 0, width: window.innerWidth, height: window.innerHeight });
type Box = ReturnType<typeof viewportRect>;
const frame = (r: Box, radius: number): Keyframe => ({ left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px`, borderRadius: `${radius}px` });

function Overview() {
  const app = useApp();
  const session = useSession((s) => s);
  const ref = useRef<HTMLDivElement>(null);
  const zoom = useRef<HTMLDivElement>(null);
  const leaving = useRef(false);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ kind: 'space'; id: string; anchor: HTMLElement } | { kind: 'doc'; id: string; anchor: HTMLElement } | null>(null);
  const [returnTo] = useState(() => document.activeElement as HTMLElement | null);

  // Arrival: the page you were writing on shrinks into its card; the others rise around it.
  useLayoutEffect(() => {
    const root = ref.current;
    const z = zoom.current;
    const card = root?.querySelector<HTMLElement>('.space[data-current] .space-card');
    card?.scrollIntoView({ block: 'center' });
    root?.querySelector<HTMLElement>('.space[data-current] .space-open')?.focus({ preventScroll: true });
    if (!root || !z || !card || isReduced()) return;
    const r = card.getBoundingClientRect();
    z.hidden = false;
    void play(z, [frame(viewportRect(), 0), frame({ left: r.left, top: r.top, width: r.width, height: r.height }, 22)], { duration: 420, easing: OPTIC }).then(() => {
      z.hidden = true;
    });
    void play(root.querySelector('.spaces-stage'), [{ opacity: 0, transform: 'scale(1.04)' }, { opacity: 1, transform: 'none' }], { duration: 380, easing: OPTIC });
  }, []);

  /** Leave into a workspace: its card grows back into the page. */
  const enter = (windowId: string, docId?: string) => {
    if (leaving.current) return;
    leaving.current = true;
    app.focusWindow(windowId);
    if (docId) app.activate(docId, { focus: false });
    const finish = () => {
      patchUi({ workspaces: false });
      requestAnimationFrame(() => app.focusEditor(windowId));
    };
    const card = ref.current?.querySelector<HTMLElement>(`.space[data-id="${windowId}"] .space-card`);
    const z = zoom.current;
    if (!card || !z || isReduced()) return finish();
    const r = card.getBoundingClientRect();
    z.hidden = false;
    void play(ref.current?.querySelector('.spaces-stage'), [{ opacity: 1 }, { opacity: 0 }], { duration: 260, easing: RECEDE });
    void play(z, [frame({ left: r.left, top: r.top, width: r.width, height: r.height }, 22), frame(viewportRect(), 0)], { duration: 360, easing: OPTIC }).then(finish);
  };

  const done = () => {
    const focused = app.session.get().focusedId;
    enter(focused);
  };

  const dismiss = () => {
    patchUi({ workspaces: false });
    requestAnimationFrame(() => returnTo?.isConnected && returnTo.focus({ preventScroll: true }));
  };

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || ui.get().dialog || menu || renaming) return;
      e.preventDefault();
      dismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const create = () => {
    const id = app.newWindow();
    if (id) enter(id);
  };

  /** Anything that may ask a question does it in the window, so the overview steps aside first. */
  const inWindow = (windowId: string, fn: () => void) => {
    patchUi({ workspaces: false });
    app.focusWindow(windowId);
    requestAnimationFrame(fn);
  };

  const closeDoc = (doc: DocMeta, windowId: string) => {
    if (!doc.cloud && isUnprotected(doc)) inWindow(windowId, () => void app.close(doc.id));
    else void app.close(doc.id);
  };

  const closeSpace = (w: WindowState) => {
    const asks = docsOf(session, w).some((d) => !d.cloud && isUnprotected(d));
    if (asks) inWindow(w.id, () => void app.closeWindow(w.id));
    else void app.closeWindow(w.id);
  };

  const count = session.windows.length;
  const full = count >= MAX_WINDOWS;
  const menuSpace = menu?.kind === 'space' ? session.windows.find((w) => w.id === menu.id) : null;
  const menuDoc = menu?.kind === 'doc' ? session.docs.find((d) => d.id === menu.id) : null;
  const menuDocOwner = menuDoc ? session.windows.find((w) => w.docIds.includes(menuDoc.id)) : null;

  return (
    <div ref={ref} className="spaces" role="dialog" aria-modal="true" aria-label="Workspaces">
      <div className="spaces-stage">
        <header className="spaces-head">
          <div>
            <h2 className="spaces-title">Workspaces</h2>
            <p className="spaces-sub">{count === 1 ? 'One place for your writing. Add another to keep a second context apart.' : `${count} contexts, each with its own documents.`}</p>
          </div>
          <button type="button" className="spaces-done" onClick={done}>
            Done
          </button>
        </header>
        <ol className="spaces-list">
          {session.windows.map((w, i) => (
            <Space
              key={w.id}
              w={w}
              index={i}
              session={session}
              current={w.id === session.focusedId}
              renaming={renaming === w.id}
              onEnter={enter}
              onRename={(name) => {
                app.renameWorkspace(w.id, name);
                setRenaming(null);
              }}
              onCancelRename={() => setRenaming(null)}
              onSpaceMenu={(anchor) => setMenu({ kind: 'space', id: w.id, anchor })}
              onDocMenu={(id, anchor) => setMenu({ kind: 'doc', id, anchor })}
              onCloseDoc={(d) => closeDoc(d, w.id)}
            />
          ))}
        </ol>
        <div className="spaces-foot">
          <button type="button" className="spaces-new" aria-disabled={full || undefined} onClick={() => !full && create()}>
            <PlusIcon size={17} />
            <span>{full ? `Up to ${MAX_WINDOWS} workspaces` : 'New Workspace'}</span>
          </button>
        </div>
      </div>
      <div ref={zoom} className="spaces-zoom" hidden aria-hidden="true" />

      <Popover open={!!menuSpace} anchor={menu?.kind === 'space' ? menu.anchor : null} placement="below-end" onClose={() => setMenu(null)} label="Workspace" role="menu">
        {menuSpace && (
          <div className="menu" onKeyDown={menuKeys}>
            <MenuItem
              label="Rename…"
              onSelect={() => {
                setMenu(null);
                setRenaming(menuSpace.id);
              }}
            />
            <MenuItem
              label="Close Workspace"
              disabled={count === 1}
              onSelect={() => {
                setMenu(null);
                closeSpace(menuSpace);
              }}
            />
          </div>
        )}
      </Popover>

      <Popover open={!!menuDoc} anchor={menu?.kind === 'doc' ? menu.anchor : null} placement="below-end" onClose={() => setMenu(null)} label="Document" role="menu">
        {menuDoc && menuDocOwner && (
          <div className="menu" onKeyDown={menuKeys}>
            {session.windows.length > 1 && (
              <div className="menu-heading" aria-hidden="true">
                Move to
              </div>
            )}
            {session.windows.map((w, i) =>
              w.id === menuDocOwner.id ? null : (
                <MenuItem
                  key={w.id}
                  label={workspaceName(w, i)}
                  onSelect={() => {
                    setMenu(null);
                    app.moveToWindow(menuDoc.id, w.id);
                  }}
                />
              ),
            )}
            <MenuItem
              label="Move to New Workspace"
              disabled={full || menuDocOwner.docIds.length < 2}
              onSelect={() => {
                setMenu(null);
                app.moveToNewWindow(menuDoc.id);
              }}
            />
            <div className="menu-sep" role="separator" />
            <MenuItem
              label="Close Document"
              onSelect={() => {
                setMenu(null);
                closeDoc(menuDoc, menuDocOwner.id);
              }}
            />
          </div>
        )}
      </Popover>
    </div>
  );
}

const SHOWN = 4;

interface SpaceProps {
  w: WindowState;
  index: number;
  session: SessionState;
  current: boolean;
  renaming: boolean;
  onEnter: (windowId: string, docId?: string) => void;
  onRename: (name: string) => void;
  onCancelRename: () => void;
  onSpaceMenu: (anchor: HTMLElement) => void;
  onDocMenu: (docId: string, anchor: HTMLElement) => void;
  onCloseDoc: (doc: DocMeta) => void;
}

function Space({ w, index, session, current, renaming, onEnter, onRename, onCancelRename, onSpaceMenu, onDocMenu, onCloseDoc }: SpaceProps) {
  const app = useApp();
  const docs = docsOf(session, w);
  const active = docs.find((d) => d.id === w.activeId) ?? null;
  const [expanded, setExpanded] = useState(false);
  const name = workspaceName(w, index);
  // A proof of the page: the opening lines of what you were reading, read once.
  const [proof] = useState(() => (active ? app.text(active.id).slice(0, 360) : ''));
  const visible = expanded ? docs : docs.slice(0, SHOWN);
  const more = docs.length - visible.length;
  const waiting = docs.filter(isUnprotected).length;

  return (
    <li className="space" data-id={w.id} data-current={current || undefined} style={{ animationDelay: `${Math.min(index, 6) * 40}ms` }}>
      <div className="space-card">
        <button type="button" className="space-open" aria-label={`Open ${name}${active ? `, showing ${active.name}` : ', empty'}${current ? ' (current)' : ''}`} onClick={() => onEnter(w.id)}>
          <span className="space-page" data-empty={!active || undefined}>
            {active ? (
              <>
                <span className="space-page-name">{active.name}</span>
                <span className="space-page-text" data-kind={active.kind}>
                  {proof || <em>Nothing written yet</em>}
                </span>
              </>
            ) : (
              <span className="space-page-empty">An empty page. Touch to start writing.</span>
            )}
          </span>
        </button>
        <div className="space-bar">
          {renaming ? (
            <NameField initial={w.name ?? ''} placeholder={name} onCommit={onRename} onCancel={onCancelRename} />
          ) : (
            <span className="space-name">
              {current && <span className="space-here" aria-hidden="true" />}
              {name}
            </span>
          )}
          <span className="space-meta">
            {docs.length === 0 ? 'Empty' : `${docs.length} ${docs.length === 1 ? 'document' : 'documents'}`}
            {waiting > 0 && <span className="space-waiting" aria-label={`, ${waiting} unsaved`} />}
          </span>
          <button type="button" className="icon-btn space-more" aria-label={`${name} options`} aria-haspopup="menu" onClick={(e) => onSpaceMenu(e.currentTarget)}>
            <MoreIcon size={17} />
          </button>
        </div>
        {docs.length > 0 && (
          <ul className="space-docs" aria-label={`Documents in ${name}`}>
            {visible.map((d) => {
              const { base, ext } = splitName(d.name);
              return (
                <li key={d.id} className="space-doc" data-active={d.id === w.activeId || undefined}>
                  <button type="button" className="space-doc-main" onClick={() => onEnter(w.id, d.id)}>
                    <span className="space-doc-dot" data-dirty={isUnprotected(d) || undefined} aria-hidden="true" />
                    <span className="space-doc-base">{base}</span>
                    <span className="space-doc-ext">{ext}</span>
                  </button>
                  <button type="button" className="space-doc-btn" aria-label={`${d.name} options`} aria-haspopup="menu" onClick={(e) => onDocMenu(d.id, e.currentTarget)}>
                    <MoreIcon size={15} />
                  </button>
                  <button type="button" className="space-doc-btn" aria-label={`Close ${d.name}`} onClick={() => onCloseDoc(d)}>
                    <CloseIcon size={13} />
                  </button>
                </li>
              );
            })}
            {(more > 0 || expanded) && docs.length > SHOWN && (
              <li className="space-doc-more">
                <button type="button" onClick={() => setExpanded(!expanded)}>
                  {expanded ? 'Show fewer' : `${more} more`}
                </button>
              </li>
            )}
          </ul>
        )}
      </div>
    </li>
  );
}

function NameField({ initial, placeholder, onCommit, onCancel }: { initial: string; placeholder: string; onCommit: (name: string) => void; onCancel: () => void }) {
  const [value, setValue] = useState(initial);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const key = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onCommit(value);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  };
  return <input ref={ref} className="space-name-field" aria-label="Workspace name" placeholder={placeholder} maxLength={60} value={value} enterKeyHint="done" onChange={(e) => setValue(e.target.value)} onKeyDown={key} onBlur={() => onCommit(value)} />;
}
